import { format } from "date-fns";
import { t } from "i18next";

import { daysLeft, isTrialing } from "@/hooks/useSubscription";

import { TFeatureFlag } from "@/types";
import { TPlan, TSubscription } from "@/types/TSubscription";

const formatDate = (iso: string | null) =>
	iso ? format(new Date(iso), "dd/MM/yyyy") : "";

/** Cents → "9,00 €" in the plan's currency. */
export const formatPrice = (cents: number, currency: string): string =>
	new Intl.NumberFormat(undefined, {
		style: "currency",
		currency: currency.toUpperCase(),
	}).format(cents / 100);

/** The one line that says where the subscription stands. */
export const subscriptionStatusLine = (
	subscription: TSubscription | null,
	planCode: string
): { text: string; color: "info" | "success" | "warning" | "neutral" } => {
	if (planCode === "enterprise") {
		return { text: t("subscription.current.enterprise"), color: "info" };
	}
	if (!subscription) {
		return { text: t("subscription.current.free"), color: "neutral" };
	}
	if (isTrialing(subscription)) {
		return {
			text: t("subscription.current.trialing", {
				count: daysLeft(subscription.trialEndsAt),
			}),
			color: "info",
		};
	}
	switch (subscription.status) {
		case "past_due":
			return {
				text: t("subscription.current.pastDue"),
				color: "warning",
			};
		case "expired":
			return {
				text: t("subscription.current.trialEnded"),
				color: "neutral",
			};
		case "trialing":
		case "active":
			if (subscription.cancelAtPeriodEnd) {
				return {
					text: t("subscription.current.cancelAtPeriodEnd", {
						date: formatDate(subscription.currentPeriodEnd),
					}),
					color: "warning",
				};
			}
			return {
				text: subscription.currentPeriodEnd
					? t("subscription.current.active", {
							date: formatDate(subscription.currentPeriodEnd),
						})
					: t("subscription.current.activeManual"),
				color: "success",
			};
		default:
			// canceled, unpaid, incomplete…: the plan in force is free.
			return {
				text:
					planCode === "free"
						? t("subscription.current.canceled")
						: t("subscription.current.activeManual"),
				color: "neutral",
			};
	}
};

/**
 * "Next invoice: date · amount" for a paid plan that renews; nothing on
 * a trial, a scheduled cancellation, or anything Stripe is not charging.
 * The amount is the plan's display price for the interval, not a Stripe
 * lookup: what the customer agreed to.
 */
export const nextInvoiceLine = (
	subscription: TSubscription | null,
	plan: TPlan
): string | null => {
	if (
		!subscription?.hasCustomer ||
		subscription.status !== "active" ||
		subscription.cancelAtPeriodEnd ||
		!subscription.currentPeriodEnd ||
		!subscription.interval
	) {
		return null;
	}
	const price =
		subscription.interval === "month" ? plan.priceMonth : plan.priceYear;
	if (price === null) return null;
	return t("subscription.current.nextInvoice", {
		date: formatDate(subscription.currentPeriodEnd),
		amount: formatPrice(price, plan.currency),
	});
};

/**
 * What a plan gives, as the grid lists it. `on`: the features on for the
 * user (feature_flags) - a paused one (invoices, sharing) is not sold.
 */
export const planFeatures = (
	plan: TPlan,
	on: Partial<Record<TFeatureFlag, boolean>> = {}
): string[] => {
	const count = (key: string, max: number | null) =>
		max === null
			? t(`subscription.plans.features.${key}Unlimited`)
			: t(`subscription.plans.features.${key}`, { count: max });
	const features = [
		count("projects", plan.maxProjects),
		count("companies", plan.maxCompanies),
	];
	if (plan.maxClients !== 0) features.push(count("clients", plan.maxClients));
	if (plan.canExportPdf) features.push(t("subscription.plans.features.pdf"));
	if (plan.canInvoice && on.invoices)
		features.push(t("subscription.plans.features.invoices"));
	if (plan.canShare && on.sharing) {
		features.push(t("subscription.plans.features.sharing"));
		features.push(count("members", plan.maxMembers));
	}
	return features;
};
